import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import {
  connectUserToGroup,
  getGroupCard,
  getGroupUser,
  removeCardFromGroup,
  getGroupDetail,
  removeUserFromGroup,
  connectCardToGroup
} from "src/network/graphql/groupService";
import { Card } from "../cards/cardSlice";
import { getUserById } from "src/network/graphql/userService";
import { initialUserDetail } from "../userDetail/userDetailSlice";
import { immediateToast } from "izitoast-react";

export type GroupUser = {
  display_id: string;
  email: string;
  is_active: boolean;
  user_id: string;
  current_organisation_role: string;
  roles: {
    organisation_id: string;
    value: string;
  };
  attributes: {
    first_name: string;
    last_name: string;
  };
};

export type GroupWallet = {
  description: string;
  display_id: string;
  name: string;
  wallet_id: string;
  wallet_type: string;
  wallet_status: string;
  balance: number;
};

export type GroupDetail = {
  group_id: string;
  name: string;
  description: string;
  group_type: string;
  created_by: string;
  group_status: string;
  display_id: string;
  owner: {
    user_id: string;
    email: string;
    attributes: {
      first_name: string;
      last_name: string;
    };
  };
  wallets: GroupWallet[];
  can_update_group: boolean;
};

export const initialGroupUser: GroupUser[] = [];
export const initialGroupCard: Card[] = [];
export const initialGroupDetail: GroupDetail = {
  group_id: "",
  name: "",
  description: "",
  group_type: "",
  created_by: "",
  group_status: "",
  display_id: "",
  owner: {
    user_id: "",
    email: "",
    attributes: {
      first_name: "",
      last_name: ""
    }
  },
  wallets: [],
  can_update_group: false
};

const groupDetailSlice = createSlice({
  name: "groupDetail",
  initialState: {
    loading: false,
    error: null,
    groupDetail: initialGroupDetail,
    groupUserLoading: false,
    groupCardLoading: false,
    groupOwnerLoading: false,
    groupUser: {
      list: initialGroupUser,
      total: 0
    },
    groupCard: {
      list: initialGroupCard,
      total: 0
    },
    groupOwner: initialUserDetail
  },
  reducers: {
    fetchGroupDetailStart: (state) => {
      state.loading = true;
    },
    fetchGroupDetailSuccess: (state, action) => {
      state.loading = false;
      state.groupDetail = action.payload ?? initialGroupDetail;
    },
    fetchGroupDetailFail: (state, action) => {
      state.loading = false;
      state.groupDetail = initialGroupDetail;
      state.error = action.payload;
    },
    fetchGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    fetchGroupUserSuccess: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = action.payload?.list ?? [];
      state.groupUser.total = action.payload?.total ?? 0;
    },
    fetchGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = [];
      state.groupUser.total = 0;
      state.error = action.payload;
    },
    fetchGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    fetchGroupCardSuccess: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = action.payload?.list ?? [];
      state.groupCard.total = action.payload?.total ?? 0;
    },
    fetchGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = [];
      state.groupCard.total = 0;
      state.error = action.payload;
    },
    fetchGroupOwnerStart: (state) => {
      state.groupOwnerLoading = true;
    },
    fetchGroupOwnerSuccess: (state, action) => {
      state.groupOwnerLoading = false;
      state.groupOwner = action.payload ?? initialUserDetail;
    },
    fetchGroupOwnerFail: (state, action) => {
      state.groupOwnerLoading = false;
      state.groupOwner = initialUserDetail;
      state.error = action.payload;
    },
    connectGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    connectGroupUserSuccess: (state) => {
      state.groupUserLoading = false;
    },
    connectGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.error = action?.payload;
    },
    deleteGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    deleteGroupUserSuccess: (state) => {
      state.groupUserLoading = false;
    },
    deleteGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.error = action?.payload;
    },
    connectGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    connectGroupCardSuccess: (state) => {
      state.groupCardLoading = false;
    },
    connectGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.error = action?.payload;
    },
    deleteGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    deleteGroupCardSuccess: (state) => {
      state.groupCardLoading = false;
    },
    deleteGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.error = action?.payload;
    }
  }
});

const {
  fetchGroupDetailFail,
  fetchGroupDetailStart,
  fetchGroupDetailSuccess,
  fetchGroupUserFail,
  fetchGroupUserStart,
  fetchGroupUserSuccess,
  fetchGroupCardFail,
  fetchGroupCardStart,
  fetchGroupCardSuccess,
  fetchGroupOwnerStart,
  fetchGroupOwnerFail,
  fetchGroupOwnerSuccess,
  connectGroupUserStart,
  connectGroupUserSuccess,
  connectGroupUserFail,
  deleteGroupUserStart,
  deleteGroupUserSuccess,
  deleteGroupUserFail,
  connectGroupCardStart,
  connectGroupCardSuccess,
  connectGroupCardFail,
  deleteGroupCardStart,
  deleteGroupCardSuccess,
  deleteGroupCardFail
} = groupDetailSlice.actions;

export const fetchGroupDetailAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupDetailStart());
    try {
      const response = await getGroupDetail(groupId);

      dispatch(fetchGroupDetailSuccess(response.data.get_group));
    } catch (error) {
      dispatch(fetchGroupDetailFail(error));
    }
  };
};

export const getGroupUserAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupUserStart());
    try {
      const response = await getGroupUser(id);

      dispatch(
        fetchGroupUserSuccess({
          list: response.data.get_group_users,
          total: response.data.get_group_users.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupUserFail(err));
    }
  };
};

export const getGroupCardAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupCardStart());
    try {
      const response = await getGroupCard(id);

      const structuredResponse = response.data.get_group_cards?.map(
        (card: any) =>
          ({
            id: card.card_id,
            bsb: card.bsb,
            cardStatus: card.card_status,
            accessType: card.card_type,
            cardNumber: card.ppan,
            orderId: card.order_id,
            clientId: card.clientId,
            cardLimit: card.card_limit,
            cardHolderName: card.holder_name,
            created_at: card.created_at,
            user: {
              id: card?.user_id,
              firstName: card?.user?.attributes?.first_name ?? "---",
              lastName: card?.user?.attributes?.last_name ?? " ",
              email: card?.user?.email ?? " "
            }
          }) as Card
      );

      dispatch(
        fetchGroupCardSuccess({
          list: structuredResponse,
          total: response.data.get_group_cards.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupCardFail(err));
    }
  };
};

export const fetchGroupOwnerAction = (data: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupOwnerStart());
    try {
      const response = await getUserById({ user_id: data });
      const user = response.data.get_user;

      dispatch(
        fetchGroupOwnerSuccess({
          user: {
            id: user.user_id,
            cognitoUserName: user.cognito_username,
            initial: user.attributes.initial,
            firstName: user.attributes.first_name,
            lastName: user.attributes.last_name,
            email: user.email,
            userName: user.username,
            gender: user.attributes.gender,
            dob: user.attributes.date_of_birth,
            role: user.roles,
            phoneNumber: user.phone,
            line1: user.attributes.address.line_1,
            line2: user.attributes.address.line_2,
            city: user.attributes.address.city,
            state: user.attributes.address.state,
            zipcode: user.attributes.address.post_code,
            country: user.attributes.address.country,
            active: user.is_active,
            kycStatus: user.security_status,
            guardianId: user.guardian_id,
            profileImage: user.attributes.profile_image,
            displayId: user.display_id,
            preferences: {
              ndis_number: user.preferences.ndis_number
            }
          }
        })
      );
    } catch (error) {
      dispatch(fetchGroupOwnerFail(error));
    }
  };
};

export const connectGroupUserAction = (groupId: string, userId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectGroupUserStart());
    try {
      await connectUserToGroup(groupId, userId);

      dispatch(connectGroupUserSuccess());
      immediateToast("success", {
        message: "User Connect to Group Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(connectGroupUserFail(err));
    }
  };
};

export const deleteGroupUserAction = (groupId: string, userId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deleteGroupUserStart());
    try {
      await removeUserFromGroup(groupId, userId);

      dispatch(deleteGroupUserSuccess());
      immediateToast("success", {
        message: "User removed from Group Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(deleteGroupUserFail(err));
    }
  };
};

export const connectGroupCardAction = (groupId: string, cardId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectGroupCardStart());
    try {
      await connectCardToGroup(groupId, cardId);

      dispatch(connectGroupCardSuccess());
      immediateToast("success", {
        message: "Card Connect to Group Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(connectGroupCardFail(err));
    }
  };
};

export const deleteGroupCardAction = (groupId: string, cardId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deleteGroupCardStart());
    try {
      await removeCardFromGroup(groupId, cardId);

      dispatch(deleteGroupCardSuccess());
      immediateToast("success", {
        message: "Card removed from Group Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(deleteGroupCardFail(err));
    }
  };
};

export default groupDetailSlice.reducer;
